/**
 * Composite scorecard overviews (FEAT-ANALYTICS-002): Acquisition overview &
 * Engagement overview — GA-style grids of cards + a cohort-retention heatmap.
 */
import React from 'react';
import { Stack, SimpleGrid, Box, Flex, Text } from '@chakra-ui/react';
import { useReport } from './api';
import {
  StatCard, StatGrid, Panel, TrendChart, BreakdownCard, ReportFrame,
  fmtInt, fmtPct, fmtDuration,
} from './components';
import { CohortGrid } from './CohortGrid';
import { countryName } from './geo';

const trendOf = (ts = []) => ts.map((r) => Number(r.value || 0));
const catsOf = (ts = []) => ts.map((r) => String(r.bucket || '').replace(/ 00:00:00$/, '').slice(5));

export function AcquisitionOverviewReport({ params }) {
  const { data, loading, error } = useReport('acquisition-overview', params);
  const k = data?.kpis || {};
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 3 }}>
            <StatCard label="New users" value={fmtInt(k.new_users)} spark={trendOf(data.trend)} />
            <StatCard label="Returning users" value={fmtInt(k.returning_users)} />
            <StatCard label="Total users" value={fmtInt(k.users)} />
          </StatGrid>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <BreakdownCard title="New users by channel" rows={data.newByChannel} labelHeader="Channel" valueHeader="New users" />
            <BreakdownCard title="Sessions by source" rows={data.bySource} labelHeader="Source" valueHeader="Sessions" />
            <BreakdownCard title="Active users by city" rows={data.byCity} labelHeader="City" />
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <BreakdownCard
              title="Key events by platform"
              rows={(data.keyEventsByPlatform || [])}
              labelHeader="Platform" valueHeader="Key events"
            />
            <Panel title="User activity by cohort"><CohortGrid cohort={data.cohort} /></Panel>
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function AcquisitionCohortsReport({ params }) {
  const { data, loading, error } = useReport('acquisition-overview', params);
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <Panel title="User retention by acquisition cohort (weekly)">
            <Text fontSize="xs" color="gray.500" mb={3}>
              Each row is the week users were first acquired; cells show the % still active N weeks later.
            </Text>
            <CohortGrid cohort={data.cohort} />
          </Panel>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
            <BreakdownCard title="New users by acquisition channel" rows={data.newByChannel} labelHeader="Channel" valueHeader="New users" />
            <BreakdownCard title="Acquisition source" rows={data.bySource} labelHeader="Source" valueHeader="Sessions" />
          </SimpleGrid>
        </Stack>
      )}
    </ReportFrame>
  );
}

export function EngagementOverviewReport({ params }) {
  const { data, loading, error } = useReport('engagement-overview', params);
  const k = data?.kpis || {};
  const st = data?.stickiness || {};
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 4 }}>
            <StatCard label="Engaged sessions" value={fmtInt(k.engaged_sessions)} spark={trendOf(data.engagedTrend)} />
            <StatCard label="Engagement rate" value={fmtPct(k.engagement_rate)} />
            <StatCard label="Avg. engagement" value={fmtDuration(k.avg_engagement_seconds)} />
            <StatCard label="Views" value={fmtInt(k.views)} help={`${fmtInt(k.event_count)} events`} />
          </StatGrid>
          <Panel title="Engaged sessions over time">
            <TrendChart categories={catsOf(data.engagedTrend)} series={[{ name: 'Engaged sessions', data: trendOf(data.engagedTrend) }]} />
          </Panel>
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <BreakdownCard title="Views by page / screen" rows={data.viewsByPage} labelHeader="Page / screen" valueHeader="Views" />
            <BreakdownCard title="Event count by event" rows={data.eventByName} labelHeader="Event" valueHeader="Count" />
            <Panel title="User stickiness">
              <SimpleGrid columns={3} spacing={2} mb={3}>
                {[['DAU', st.dau], ['WAU', st.wau], ['MAU', st.mau]].map(([lbl, v]) => (
                  <Box key={lbl} textAlign="center">
                    <Text fontSize="10px" color="gray.500" fontWeight="700">{lbl}</Text>
                    <Text fontSize="20px" fontWeight="700" color="gray.900">{fmtInt(v)}</Text>
                  </Box>
                ))}
              </SimpleGrid>
              <Flex justify="space-between" fontSize="xs" color="gray.500" borderTop="1px solid" borderColor="gray.100" pt={2}>
                <Text>DAU/MAU <b>{st.dau_mau ?? 0}%</b></Text>
                <Text>DAU/WAU <b>{st.dau_wau ?? 0}%</b></Text>
                <Text>WAU/MAU <b>{st.wau_mau ?? 0}%</b></Text>
              </Flex>
            </Panel>
          </SimpleGrid>
          <Panel title="User retention by cohort"><CohortGrid cohort={data.cohort} /></Panel>
        </Stack>
      )}
    </ReportFrame>
  );
}
